import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Help from '@material-ui/icons/Help'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'

const useStyles = makeStyles((theme) => ({
    ...componentStyles,
    pulse: {
        animation: `$pulseAnim 0.9s ease`,
    },
    '@keyframes pulseAnim': {
        '0%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 rgba(34, 150, 38, 0)',
            backgroundColor: '#fff',
        },
        '40%': {
            transform: 'scale(1.08)',
            boxShadow: '0 0 32px 8px rgba(34, 150, 38, 0.25)',
            backgroundColor: '#e6ffe6', // light green flash
        },
        '100%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 rgba(34, 150, 38, 0)',
            backgroundColor: '#fff',
        }
    }
}))

function HelperCard(props) {
    const classes = { ...useStyles() }

    const [animate, setAnimate] = useState(false)

    useEffect(() => {
        // trigger animation when stage or subsection changes
        setAnimate(true)
        const timer = setTimeout(() => setAnimate(false), 600)
        return () => clearTimeout(timer)
    }, [props.stage, props.aboutCompanyInput])

    const tips = {
        primaryPerson: {
            title: "Tip for adding your admins",
            tips: [
                'These people have full control over your profile. You can add additional users later in the process if you would like other members of your team to have access'
            ]
        },
        coverage: {
            title: 'Select your coverage area',
            tips: [
                'Choose the counties OR draw eircode areas you serve',
                'You will only receive leads originating from the counties or eircode areas you select',
                'Add all areas you cover to maximize lead opportunities',
                'You can update coverage later from your Profile'
            ]
        },
        companyDetails: {
            title: "Tips for completing your company profile",
            tips: [
                'These attributes will be available for people to see on your profile later',
                'You must fill in each section to continue',
                'You can display/hide this information at the next stage'
            ]
        },
        previewRecommendation: {
            title: "Tips for completing your company preview",
            tips: [
                'You can choose which information you want to display using the boxes below',
                'This is what new users will see when your company is listed beside others'
            ]
        },
        aboutCompany: {
            about: {
                title: "Tips for a good About section",
                tips: [
                    'Describe what your company does',
                    'Briefly, what’s your mission / purpose',
                    'Include a unique selling point, or point of difference'
                ]
            },
            whyChoose: {
                title: "Tips for a good Choose You section",
                tips: [
                    'Why would a customer choose you',
                    'Mention past experience with grant application and successful installations',
                    'Talk about the team, quality standards, the technology, any warranties',
                    'Mention you are regulated by SEAI and fully insured',
                    'Mention the areas you cover'
                ]
            },
            customerJourney: {
                title: "Tips for a good Customer Journey",
                tips: [
                    'Step 1: What happens when you receive their enquiry, who contacts them',
                    'Step 2: Site survey? what does this involve',
                    'Step 3: Proposal? when does this arrive',
                    'Step 4: Follow up questions?',
                    'Step 5: Grant application?',
                    'Note: the above steps are just a rough guideline, please detail your process in detail so customers know what to expect'
                ]
            }
        },
        counties: {
            title: "Tips for adding counties",
            tips: [
                'Add all the counties you offer installations in.',
                'You will show up in search results and recommendations for these counties.'
            ]
        },
        profileOptional: {
            title: 'Profile completion is optional',
            tips: [
                'You can receive leads without completing the rest of your profile',
                'Only the Coverage section is required in order to purchase leads',
                'Completing your profile creates a public provider page on EnergyEfficiency.ie showcasing your company',
                'A richer profile (about, technology, case studies) can improve conversion and trust',
                'Update these sections whenever you like – there is no deadline'
            ]
        },
        technology: {
            title: "Tips for adding technology",
            tips: [
                'Select your primary/preferred brand for each of the technologies listed.',
                'These will be displayed on your profile.'
            ]
        },
        projects: {
            title: "Tips for good case studies",
            tips: [
                'Add as many case studies as you like, we recommend adding at least 2.',
                'We strongly recommend adding a photo case study and a video case study, people want to see your past work'
            ]
        },
        plan: {
            title: "Tip for selecting your plan",
            tips: [
                'Contract duration is monthly. Free anytime cancellation. See terms & conditions for more information.'
            ]
        },
        survey: {
            title: "Tip for completing the survey",
            tips: [
                'If you participate in our survey, you will receive a copy of the industry aggregate data gathered from installers each year to every company which is registered on EnergyEfficiency.ie. This allows you to contribute to and benefit from industry insights.'
            ]
        },
        companyVerification: {
            title: "About your documents",
            tips: [
                "We verify that your business is real, active, and insured – so homeowners know they’re dealing with a professional. Upload clear documents to avoid delays and help us approve your profile faster.",
                "SEAI verification is carried out by our team once you submit your other documents."
            ]
        },
        websiteVerification: {
            title: "Tips for adding your badge",
            tips: [
                "Make sure the badge is added to your homepage or footer, this is where we check when attempting to detect the badge.",
                "This badge must remain on the homepage of your site in order for your profile on Energy Efficiency to remain active. If it is removed your profile will be suspended until it is restored."
            ]
        },
        delivery: {
            title: "Tip for choosing your delivery method",
            tips: [
                'This determines how your leads will be sent to you. We recommend CRM as leads are generally dealt with more efficiently when they are added directly to your CRM.'
            ]
        },
        payment: {
            title: "Tip for choosing your payment method",
            tips: [
                'Payment is weekly in arrears: Saturday–Friday delivery week, charged the following Monday. You only pay for leads actually delivered.'
            ]
        }
    }    

    function renderTips(stage, aboutCompanyInput) {
        if (!tips[stage]) return null;
    
        let sectionTitle = '';
        let sectionTips = [];
    
        if (stage === 'aboutCompany') {
            const subsection = tips.aboutCompany[aboutCompanyInput];
            if (!subsection) return null;
            sectionTitle = subsection.title;
            sectionTips = subsection.tips;
        } else {
            sectionTitle = tips[stage].title;
            sectionTips = tips[stage].tips;
        }
    
        return (
            <>
                <Grid item xs={12} style={{marginBottom: '1rem'}}>
                    <Box className='help-top' >
                        <Help className='helper-icon' />
                        <h3 className='help-heading'>{sectionTitle}</h3>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box className='help-content'>
                        <ul>
                            {sectionTips.map((tip, index) => <li key={index}>{tip}</li>)}
                        </ul>
                    </Box>
                </Grid>
            </>

        );
    }    

    return (
        <Card className={animate ? classes.pulse : ''}>
            <CardContent>
                <Grid container>
                    {renderTips(props.stage, props.aboutCompanyInput)}
                </Grid>
            </CardContent>  
        </Card>
    );
}

export default HelperCard